import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Calendar, MapPin, Ticket, Plus, X, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const COUNTRIES = ["Madagascar", "Sénégal", "Nigeria", "Côte d'Ivoire", "Cameroun", "RDC", "Mali", "Afrique du Sud", "Kenya", "Ghana", "Maroc", "Tunisie", "France"];

export const EventsPage = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [country, setCountry] = useState("Tous");
  const [form, setForm] = useState({
    title: "", description: "", venue: "", city: "", country: "Madagascar", date: "", ticket_url: "", cover_url: "",
  });

  const load = () => {
    const params = {};
    if (country !== "Tous") params.country = country;
    api.get("/events", { params }).then(({ data }) => setEvents(data)).catch(() => {});
  };

  useEffect(() => { load(); }, [country]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/events", form);
      toast.success("Évènement publié !");
      setShowForm(false);
      setForm({ title: "", description: "", venue: "", city: "", country: "Madagascar", date: "", ticket_url: "", cover_url: "" });
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet évènement ?")) return;
    await api.delete(`/events/${id}`);
    load();
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-7xl mx-auto pb-32" data-testid="events-page">
      <p className="inline-flex items-center gap-2 tag-pill mb-5 bg-brand-red/15 border-brand-red/40 text-brand-red">
        <Calendar size={12} /> Agenda
      </p>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-balance">Concerts & évènements</h1>
          <p className="text-white/70 text-lg mt-3">La scène live de Madagascar et d'Afrique.</p>
        </div>
        {user?.is_artist && (
          <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2 w-max" data-testid="events-new-btn">
            <Plus size={14} /> Publier mon concert
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {["Tous", ...COUNTRIES].map((c) => (
          <button key={c} onClick={() => setCountry(c)} className={`tag-pill ${country === c ? "active" : ""}`} data-testid={`event-filter-${c}`}>
            {c}
          </button>
        ))}
      </div>

      {events.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
          <Calendar size={32} className="mx-auto text-white/40 mb-3" />
          <p className="text-white/60">Aucun évènement à venir pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-4 stagger">
          {events.map((ev) => (
            <div key={ev.event_id} className="group flex flex-col md:flex-row gap-4 p-5 border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] rounded-2xl transition-colors" data-testid={`event-card-${ev.event_id}`}>
              <div className="md:w-32 shrink-0 text-center md:text-left">
                <DateBadge date={ev.date} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-xl font-black mb-1">{ev.title}</h3>
                <p className="text-sm text-white/60 mb-1">
                  Par <Link to={`/artist/${ev.user_id}`} className="text-brand-orange hover:text-brand-red">{ev.artist_name}</Link>
                </p>
                <p className="text-sm text-white/70 flex items-center gap-2 flex-wrap">
                  <MapPin size={14} className="text-brand-red" /> {ev.venue}{ev.venue && ev.city && ","} {ev.city} · {ev.country}
                </p>
                {ev.description && <p className="text-sm text-white/60 mt-2 line-clamp-2">{ev.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                {ev.ticket_url && (
                  <a href={ev.ticket_url} target="_blank" rel="noreferrer" className="btn-primary text-sm inline-flex items-center gap-1" data-testid={`ticket-btn-${ev.event_id}`}>
                    <Ticket size={14} /> Billets
                  </a>
                )}
                {(user?.user_id === ev.user_id || user?.is_admin) && (
                  <button onClick={() => remove(ev.event_id)} className="text-red-400 hover:text-red-300 p-2" data-testid={`delete-event-${ev.event_id}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="event-form-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full md:max-w-2xl bg-brand-surface border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl font-black">Nouveau concert</h3>
              <button onClick={() => setShowForm(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
              <Field label="Titre *" full><Input value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="event-title" /></Field>
              <Field label="Date *"><Input type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required testid="event-date" /></Field>
              <Field label="Pays">
                <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none" data-testid="event-country-select">
                  {COUNTRIES.map((c) => <option key={c} value={c} className="bg-brand-dark">{c}</option>)}
                </select>
              </Field>
              <Field label="Ville"><Input value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="event-city" /></Field>
              <Field label="Lieu (salle, club...)"><Input value={form.venue} onChange={(v) => setForm({ ...form, venue: v })} testid="event-venue" /></Field>
              <Field label="URL billetterie" full><Input value={form.ticket_url} onChange={(v) => setForm({ ...form, ticket_url: v })} placeholder="https://..." testid="event-ticket" /></Field>
              <Field label="Description" full>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none min-h-[80px]" data-testid="event-desc-input" />
              </Field>
              <button type="submit" className="btn-primary md:col-span-2 inline-flex items-center justify-center gap-2" data-testid="event-submit-btn">
                <Plus size={14} /> Publier l'évènement
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const DateBadge = ({ date }) => {
  const d = date ? new Date(date) : null;
  if (!d) return null;
  const month = d.toLocaleDateString("fr-FR", { month: "short" }).toUpperCase().replace(".", "");
  return (
    <div className="inline-block rounded-xl bg-gradient-to-br from-brand-red to-brand-orange text-white px-4 py-3">
      <p className="font-display text-3xl font-black leading-none">{d.getDate()}</p>
      <p className="text-[10px] tracking-widest mt-1">{month} {d.getFullYear()}</p>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-[10px] tracking-[0.25em] uppercase text-white/60 font-bold mb-2">{label}</label>
    {children}
  </div>
);
const Input = ({ type = "text", value, onChange, placeholder, testid, required }) => (
  <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-brand-red/50" data-testid={`${testid}-input`} />
);

export default EventsPage;
